// The chart of accounts and the journal builders (06 §6.2–6.3, 14 §14.4).
// Pure and exact: integer minor units, every builder returns lines that balance to the minor
// unit or throws. The server turns a draft into a `journals/{id}` document; nothing here knows Firestore.
import { allocateMoney, money, vatFromInclusive, type CurrencyCode } from './money';
import { isSafeDocId } from './ids';

// ---------------------------------------------------------------- chart

export type AccountType =
  | 'asset'
  | 'contra_asset'
  | 'liability'
  | 'equity'
  | 'revenue'
  | 'contra_revenue'
  | 'cogs'
  | 'opex'
  | 'other';
export type NormalSide = 'debit' | 'credit';
export type MoneyKind = 'cargo' | 'running';
export type MoneySide = 'origin' | 'destination' | 'either';

export interface Account {
  readonly code: string;
  readonly name: string;
  readonly type: AccountType;
  readonly normal: NormalSide;
  /** Cargo money exists because cargo moved; running money keeps a location open (14 §14.3). */
  readonly kind: MoneyKind;
  readonly side: MoneySide;
}

const acc = (
  code: string,
  name: string,
  type: AccountType,
  kind: MoneyKind,
  side: MoneySide,
): Account => ({
  code,
  name,
  type,
  kind,
  side,
  normal: type === 'liability' || type === 'equity' || type === 'revenue' ? 'credit' : 'debit',
});
const contraAsset = (code: string, name: string): Account => ({
  ...acc(code, name, 'contra_asset', 'running', 'either'),
  normal: 'credit',
});

/** The starting standard chart, per entity (06 §6.2). Codes are stable. */
export const CHART_OF_ACCOUNTS: readonly Account[] = [
  acc('1000', 'Cash on hand', 'asset', 'cargo', 'either'),
  acc('1001', 'Driver cash in custody', 'asset', 'cargo', 'either'),
  acc('1003', 'Partner collections receivable', 'asset', 'cargo', 'destination'),
  acc('1005', 'Cheques on hand', 'asset', 'cargo', 'either'),
  acc('1010', 'Bank', 'asset', 'cargo', 'either'),
  acc('1015', 'Reserve bank account', 'asset', 'cargo', 'either'),
  acc('1020', 'Card / gateway clearing', 'asset', 'cargo', 'either'),
  acc('1040', 'VAT input', 'asset', 'cargo', 'either'),
  acc('1100', 'Accounts receivable, customers', 'asset', 'cargo', 'either'),
  acc('1150', 'Empty box inventory', 'asset', 'cargo', 'origin'),
  acc('1200', 'Intercompany receivable', 'asset', 'cargo', 'either'),
  acc('1500', 'Fixed assets', 'asset', 'running', 'either'),
  contraAsset('1590', 'Accumulated depreciation'),
  acc('2000', 'Accounts payable', 'liability', 'cargo', 'either'),
  acc('2010', 'Customer deposits / unearned', 'liability', 'cargo', 'origin'),
  acc('2020', 'Rewards liability', 'liability', 'cargo', 'origin'),
  acc('2030', 'VAT output', 'liability', 'cargo', 'either'),
  acc('2040', 'Duties and taxes collected for customs', 'liability', 'cargo', 'destination'),
  acc('2050', 'Accrued shipping costs', 'liability', 'cargo', 'either'),
  acc('2060', 'Commissions payable', 'liability', 'cargo', 'origin'),
  acc('2100', 'Intercompany payable', 'liability', 'cargo', 'either'),
  acc('2200', 'Loans received', 'liability', 'running', 'either'),
  acc('3000', 'Owner equity', 'equity', 'running', 'either'),
  acc('3100', 'Opening balance equity', 'equity', 'running', 'either'),
  acc('3200', 'Retained earnings', 'equity', 'running', 'either'),
  acc('3300', 'Translation reserve (CTA)', 'equity', 'running', 'either'),
  acc('4000', 'Freight revenue', 'revenue', 'cargo', 'origin'),
  acc('4010', 'Add-on revenue (insurance, packing, door delivery)', 'revenue', 'cargo', 'either'),
  acc('4100', 'Empty box revenue', 'revenue', 'cargo', 'origin'),
  {
    ...acc('4800', 'Discounts and vouchers', 'contra_revenue', 'cargo', 'origin'),
    normal: 'debit',
  },
  acc('4900', 'Other income', 'revenue', 'running', 'either'),
  acc('5010', 'Freight linehaul (ocean / air)', 'cogs', 'cargo', 'origin'),
  acc('5020', 'Telex / documents', 'cogs', 'cargo', 'origin'),
  acc('5030', 'Customs brokerage and duties', 'cogs', 'cargo', 'destination'),
  acc('5040', 'Port storage, demurrage, detention', 'cogs', 'cargo', 'destination'),
  acc('5050', 'Trucking', 'cogs', 'cargo', 'either'),
  acc('5055', 'Box forwarding (partner haulers, couriers)', 'cogs', 'cargo', 'destination'),
  acc('5060', 'Final mile', 'cogs', 'cargo', 'destination'),
  acc('5070', 'Empty box cost of sales', 'cogs', 'cargo', 'origin'),
  acc('5080', 'Claims and write-offs (lost, damaged)', 'cogs', 'cargo', 'either'),
  acc('5090', 'Cargo insurance premiums', 'cogs', 'cargo', 'origin'),
  acc('5200', 'FX gain/loss', 'other', 'running', 'either'),
  acc('6010', 'Rent', 'opex', 'running', 'either'),
  acc('6020', 'Salaries and allowances', 'opex', 'running', 'either'),
  acc('6025', 'Visas and staff costs', 'opex', 'running', 'origin'),
  acc('6030', 'Utilities and internet', 'opex', 'running', 'either'),
  acc('6040', 'Fuel and vehicle', 'opex', 'running', 'either'),
  acc('6050', 'Office and supplies', 'opex', 'running', 'either'),
  acc('6060', 'Marketing', 'opex', 'running', 'either'),
  acc('6100', 'Bank charges', 'opex', 'running', 'either'),
  acc('6200', 'Cash over/short', 'opex', 'running', 'either'),
  acc('6300', 'Commissions', 'opex', 'running', 'origin'),
];

const ACCOUNT_BY_CODE: ReadonlyMap<string, Account> = new Map(
  CHART_OF_ACCOUNTS.map((a) => [a.code, a]),
);

export function getAccount(code: string): Account | undefined {
  return ACCOUNT_BY_CODE.get(code);
}

export function isKnownAccount(code: string): boolean {
  return ACCOUNT_BY_CODE.has(code);
}

// ------------------------------------------------------ money-out categories

export type MoneyOutCategoryKey =
  | 'freight'
  | 'telex_bl'
  | 'customs'
  | 'port_storage'
  | 'trucking'
  | 'forwarding'
  | 'delivery'
  | 'empty_boxes'
  | 'insurance'
  | 'rent'
  | 'salaries'
  | 'visas'
  | 'utilities'
  | 'fuel_vehicle'
  | 'marketing'
  | 'office'
  | 'bank_charges'
  | 'other';

export interface CategoryAccount {
  readonly account: string;
  readonly kind: MoneyKind;
  readonly side: MoneySide;
  /** What staff see (14 §14.4). */
  readonly label: string;
}

const cat = (
  account: string,
  kind: MoneyKind,
  side: MoneySide,
  label: string,
): CategoryAccount => ({ account, kind, side, label });

/**
 * Friendly category → account (14 §14.4). Staff never pick a code. `empty_boxes` debits inventory
 * (1150), which is expensed to 5070 when the boxes are sold. `other` is the catch-all running cost
 * and lands in Office and supplies; 14 §14.4 lists no separate account for it.
 */
export const MONEY_OUT_CATEGORY_ACCOUNT: Readonly<Record<MoneyOutCategoryKey, CategoryAccount>> = {
  freight: cat('5010', 'cargo', 'origin', 'Freight (shipping line)'),
  telex_bl: cat('5020', 'cargo', 'origin', 'Telex release / BL charges'),
  customs: cat('5030', 'cargo', 'destination', 'Customs and brokerage'),
  port_storage: cat('5040', 'cargo', 'destination', 'Port storage / demurrage / detention'),
  trucking: cat('5050', 'cargo', 'either', 'Trucking'),
  forwarding: cat('5055', 'cargo', 'destination', 'Box forwarding (hauler)'),
  delivery: cat('5060', 'cargo', 'destination', 'Delivery fees'),
  empty_boxes: cat('1150', 'cargo', 'origin', 'Empty boxes purchased'),
  insurance: cat('5090', 'cargo', 'origin', 'Cargo insurance premium'),
  rent: cat('6010', 'running', 'either', 'Rent'),
  salaries: cat('6020', 'running', 'either', 'Salaries and allowances'),
  visas: cat('6025', 'running', 'origin', 'Visas and staff costs'),
  utilities: cat('6030', 'running', 'either', 'Utilities and internet'),
  fuel_vehicle: cat('6040', 'running', 'either', 'Fuel and vehicle'),
  marketing: cat('6060', 'running', 'either', 'Marketing'),
  office: cat('6050', 'running', 'either', 'Office and supplies'),
  bank_charges: cat('6100', 'running', 'either', 'Bank charges'),
  other: cat('6050', 'running', 'either', 'Other'),
};

// ---------------------------------------------------------------- drafts

export type Dimensions = Readonly<Record<string, string>>;

export interface JournalLineDraft {
  readonly accountCode: string;
  readonly debitMinor: number;
  readonly creditMinor: number;
  readonly dimensions: Dimensions;
}

/** A journal before the server stamps entity, dates and who posted it. */
export interface JournalDraft {
  readonly sourceType: string;
  readonly sourceId: string;
  readonly version: number;
  readonly currency: CurrencyCode;
  readonly memo: string;
  readonly lines: readonly JournalLineDraft[];
}

export class AccountingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AccountingError';
  }
}

export function journalTotals(
  lines: readonly Pick<JournalLineDraft, 'debitMinor' | 'creditMinor'>[],
): {
  debitMinor: number;
  creditMinor: number;
} {
  return lines.reduce(
    (t, l) => ({
      debitMinor: t.debitMinor + l.debitMinor,
      creditMinor: t.creditMinor + l.creditMinor,
    }),
    { debitMinor: 0, creditMinor: 0 },
  );
}

/** Debits equal credits to the minor unit; each line is one positive amount on one side (06 §6.1). */
export function assertBalanced(lines: readonly JournalLineDraft[]): void {
  if (lines.length < 2) throw new AccountingError('A journal needs at least two lines');
  for (const l of lines) {
    if (!isKnownAccount(l.accountCode)) {
      throw new AccountingError(`Unknown account ${l.accountCode}`);
    }
    for (const n of [l.debitMinor, l.creditMinor]) {
      if (!Number.isSafeInteger(n) || n < 0) {
        throw new AccountingError(
          `Account ${l.accountCode}: amounts are whole minor units, never negative`,
        );
      }
    }
    if (l.debitMinor > 0 === l.creditMinor > 0) {
      throw new AccountingError(`Account ${l.accountCode}: a line is either a debit or a credit`);
    }
  }
  const { debitMinor, creditMinor } = journalTotals(lines);
  if (debitMinor !== creditMinor) {
    throw new AccountingError(
      `Journal does not balance: debits ${debitMinor}, credits ${creditMinor}`,
    );
  }
}

/** `{entityId}__{sourceType}__{sourceId}__v{n}` (03 §3.9). The id is the idempotency check. */
export function journalId(
  entityId: string,
  sourceType: string,
  sourceId: string,
  version = 1,
): string {
  for (const part of [entityId, sourceType, sourceId]) {
    if (!isSafeDocId(part)) throw new AccountingError(`"${part}" cannot be part of a journal id`);
  }
  if (!Number.isInteger(version) || version < 1) throw new AccountingError('Version starts at 1');
  return `${entityId}__${sourceType}__${sourceId}__v${version}`;
}

/** Wraps balanced lines with their source. Returns `undefined` when there is nothing to post. */
export function journalDraft(
  meta: Omit<JournalDraft, 'lines' | 'version'> & { version?: number },
  lines: readonly JournalLineDraft[],
): JournalDraft | undefined {
  if (lines.length === 0) return undefined;
  assertBalanced(lines);
  return { version: 1, ...meta, lines };
}

// -------------------------------------------------------------- builders

function debit(accountCode: string, amountMinor: number, dimensions: Dimensions): JournalLineDraft {
  return { accountCode, debitMinor: amountMinor, creditMinor: 0, dimensions };
}
function credit(
  accountCode: string,
  amountMinor: number,
  dimensions: Dimensions,
): JournalLineDraft {
  return { accountCode, debitMinor: 0, creditMinor: amountMinor, dimensions };
}

/** Zero-amount lines are dropped: a journal never carries an empty line. */
function nonZero(lines: JournalLineDraft[]): JournalLineDraft[] {
  return lines.filter((l) => l.debitMinor > 0 || l.creditMinor > 0);
}

function checked(lines: JournalLineDraft[]): JournalLineDraft[] {
  const kept = nonZero(lines);
  if (kept.length === 0) return kept;
  assertBalanced(kept);
  return kept;
}

function requireMinor(value: number, label: string, allowZero = false): void {
  if (!Number.isSafeInteger(value) || value < 0 || (!allowZero && value === 0)) {
    throw new AccountingError(
      `${label} must be a ${allowZero ? 'non-negative' : 'positive'} whole number of minor units`,
    );
  }
}

export type PaymentMethodKey = 'cash' | 'card' | 'bank' | 'cheque' | 'wallet' | 'voucher';

/** The account that receives (or, for `wallet`, gives up) the money for each payment method (06 §6.3). */
export const PAYMENT_METHOD_ACCOUNT: Readonly<Record<PaymentMethodKey, string>> = {
  cash: '1000',
  card: '1020',
  bank: '1010',
  cheque: '1005',
  wallet: '2010',
  /** A voucher spent against an invoice debits the rewards liability (15 §15.2); see `voucherDebitAccount`. */
  voucher: '2020',
};

export interface InvoiceLineForPosting {
  readonly kind: 'freight' | 'addon' | 'box' | 'discount';
  /** VAT-inclusive. Discounts are negative. */
  readonly amountMinor: number;
}

export interface CollectionInput {
  readonly vatRatePercent: number;
  readonly currency: CurrencyCode;
  readonly invoiceLines: readonly InvoiceLineForPosting[];
  readonly dimensions: Dimensions;
}

/**
 * Shipment created: Dr 1100 gross and 4800 discounts; Cr 4000 freight, 4010 add-ons, 4100 boxes
 * and 2030 VAT. Prices are VAT-inclusive (D-18), so the VAT of the gross is backed out once and
 * spread over the revenue lines in proportion with largest remainder: the journal balances exactly
 * and the VAT line equals the VAT printed on the invoice.
 */
export function collectionLines(input: CollectionInput): JournalLineDraft[] {
  const totals = { freight: 0, addon: 0, box: 0, discount: 0 };
  for (const line of input.invoiceLines) {
    requireMinor(Math.abs(line.amountMinor), `Invoice ${line.kind} line`, true);
    if (line.kind === 'discount') {
      if (line.amountMinor > 0) throw new AccountingError('A discount line must be negative');
      totals.discount -= line.amountMinor;
    } else {
      if (line.amountMinor < 0) throw new AccountingError(`A ${line.kind} line cannot be negative`);
      totals[line.kind] += line.amountMinor;
    }
  }
  const revenue = totals.freight + totals.addon + totals.box;
  const gross = revenue - totals.discount;
  if (gross < 0) throw new AccountingError('The discount is larger than the invoice');
  if (revenue === 0) throw new AccountingError('An invoice with no charges has nothing to post');

  const vat = vatFromInclusive(money(gross, input.currency), input.vatRatePercent).vat.amountMinor;
  const discountVat = vatFromInclusive(money(totals.discount, input.currency), input.vatRatePercent)
    .vat.amountMinor;
  const revenueVat = vat + discountVat;
  const [freightVat = 0, addonVat = 0, boxVat = 0] = allocateMoney(
    money(revenueVat, input.currency),
    [totals.freight, totals.addon, totals.box],
  ).map((m) => m.amountMinor);

  const d = input.dimensions;
  return checked([
    debit('1100', gross, d),
    debit('4800', totals.discount - discountVat, d),
    credit('4000', totals.freight - freightVat, d),
    credit('4010', totals.addon - addonVat, d),
    credit('4100', totals.box - boxVat, d),
    credit('2030', vat, d),
  ]);
}

/**
 * A credit note against a shipment invoice (06 §6.3, "Shipment cancelled"): the exact reverse of
 * the collection journal for the lines being credited. Crediting every line of the invoice gives
 * the mirror image of the original journal, to the minor unit, because the VAT is backed out the
 * same way. The refund of money is a separate event; the credit only reduces what the customer owes.
 */
export function creditNoteLines(input: CollectionInput): JournalLineDraft[] {
  return collectionLines(input).map((l) => ({
    accountCode: l.accountCode,
    debitMinor: l.creditMinor,
    creditMinor: l.debitMinor,
    dimensions: l.dimensions,
  }));
}

export interface PaymentInput {
  readonly method: PaymentMethodKey;
  /** What the customer handed over. */
  readonly amountMinor: number;
  /** The part that settles the invoice (credit 1100). */
  readonly appliedMinor: number;
  /** The part kept as customer credit (credit 2010). Only when `toDeposit`. */
  readonly depositMinor?: number;
  /** Dimension on the receiving line, e.g. the till or card wallet. */
  readonly walletId?: string;
  /** Receive into this account instead of the method's own: cash a driver takes goes to 1001. */
  readonly accountCode?: string;
  readonly dimensions: Dimensions;
}

/**
 * Payment received: Dr cash/card/bank/cheque, Cr 1100; any excess Cr 2010. Paying with a
 * customer's credit debits 2010 instead of an asset account and cannot create more credit.
 */
export function paymentLines(input: PaymentInput): JournalLineDraft[] {
  const deposit = input.depositMinor ?? 0;
  requireMinor(input.amountMinor, 'Payment');
  requireMinor(input.appliedMinor, 'Applied amount', true);
  requireMinor(deposit, 'Deposit', true);
  if (input.appliedMinor + deposit !== input.amountMinor) {
    throw new AccountingError('The applied amount plus the deposit must equal the payment');
  }
  if (input.method === 'wallet' && deposit > 0) {
    throw new AccountingError('Customer credit cannot be topped up with customer credit');
  }
  const receiving: Dimensions = input.walletId
    ? { ...input.dimensions, walletId: input.walletId }
    : input.dimensions;
  return checked([
    debit(input.accountCode ?? PAYMENT_METHOD_ACCOUNT[input.method], input.amountMinor, receiving),
    credit('1100', input.appliedMinor, input.dimensions),
    credit('2010', deposit, input.dimensions),
  ]);
}

export interface EmptyBoxSaleInput {
  readonly grossMinor: number;
  readonly vatRatePercent: number;
  readonly currency: CurrencyCode;
  /** Cost of the boxes sold, for cost of sales. */
  readonly costMinor: number;
  /** Absent means a credit sale: debit receivables. */
  readonly method?: PaymentMethodKey;
  readonly walletId?: string;
  /** Receive into this account instead of the method's own (a driver's custody, 1001). */
  readonly accountCode?: string;
  readonly dimensions: Dimensions;
}

/** Empty box sale: Dr cash or 1100; Cr 4100 net and 2030 VAT; Dr 5070, Cr 1150 at cost. */
export function emptyBoxSaleLines(input: EmptyBoxSaleInput): JournalLineDraft[] {
  requireMinor(input.grossMinor, 'Sale total');
  requireMinor(input.costMinor, 'Box cost', true);
  const { net, vat } = vatFromInclusive(
    money(input.grossMinor, input.currency),
    input.vatRatePercent,
  );
  const receiving: Dimensions =
    input.method && input.walletId
      ? { ...input.dimensions, walletId: input.walletId }
      : input.dimensions;
  const account =
    input.accountCode ?? (input.method ? PAYMENT_METHOD_ACCOUNT[input.method] : '1100');
  return checked([
    debit(account, input.grossMinor, receiving),
    credit('4100', net.amountMinor, input.dimensions),
    credit('2030', vat.amountMinor, input.dimensions),
    debit('5070', input.costMinor, input.dimensions),
    credit('1150', input.costMinor, input.dimensions),
  ]);
}

export interface EmptyBoxInwardInput {
  /** What the supplier is paid: VAT-inclusive when `vat` is given. */
  readonly totalMinor: number;
  readonly vat?: VatLine | undefined;
  /** Absent means owed to the supplier (2000). */
  readonly method?: Exclude<PaymentMethodKey, 'wallet'>;
  readonly walletId?: string;
  readonly dimensions: Dimensions;
}

/** Empty boxes bought: Dr 1150 (net of VAT) and 1040; Cr 2000 or the account the money left from. */
export function emptyBoxInwardLines(input: EmptyBoxInwardInput): JournalLineDraft[] {
  requireMinor(input.totalMinor, 'Purchase total');
  const vatMinor = input.vat?.vatMinor ?? 0;
  if (vatMinor >= input.totalMinor) {
    throw new AccountingError('The VAT cannot be the whole amount: something must be left as cost');
  }
  const paying: Dimensions =
    input.method && input.walletId
      ? { ...input.dimensions, walletId: input.walletId }
      : input.dimensions;
  return checked([
    debit('1150', input.totalMinor - vatMinor, input.dimensions),
    ...vatDebit(input.vat, input.totalMinor, input.dimensions),
    credit(input.method ? PAYMENT_METHOD_ACCOUNT[input.method] : '2000', input.totalMinor, paying),
  ]);
}

export interface StockAdjustmentInput {
  /** Counted minus book, in boxes. Negative is a shortage. */
  readonly differenceUnits: number;
  readonly unitCostMinor: number;
  readonly dimensions: Dimensions;
}

/** Stock count: a shortage is Dr 5070, Cr 1150; a surplus the reverse. No difference, no lines. */
export function stockAdjustmentLines(input: StockAdjustmentInput): JournalLineDraft[] {
  if (!Number.isSafeInteger(input.differenceUnits))
    throw new AccountingError('Difference must be whole boxes');
  requireMinor(input.unitCostMinor, 'Unit cost', true);
  const amount = Math.abs(input.differenceUnits) * input.unitCostMinor;
  if (amount === 0) return [];
  const shortage = input.differenceUnits < 0;
  return checked([
    shortage ? debit('5070', amount, input.dimensions) : debit('1150', amount, input.dimensions),
    shortage ? credit('1150', amount, input.dimensions) : credit('5070', amount, input.dimensions),
  ]);
}

export interface WalletRef {
  readonly walletId: string;
  readonly accountCode: string;
}

/**
 * The supplier's VAT inside a VAT-inclusive purchase (06 §6.3 "VAT input"), already worked out by
 * `splitVatInput`. It is posted to 1040 apart from the cost; the invoice number, the supplier's VAT
 * number and the net amount ride on the 1040 line for the VAT return.
 */
export interface VatLine {
  readonly vatMinor: number;
  readonly invoiceNo: string;
  readonly supplierVatNo: string;
}

export interface VatInputSpec {
  /** Basis points: 1500 is 15%, backed out of the gross. */
  readonly rateBp?: number | undefined;
  /** The VAT printed on the supplier's invoice. */
  readonly amountMinor?: number | undefined;
}

/**
 * Splits a VAT-inclusive amount into net and VAT, from a rate or from the VAT on the invoice.
 * The VAT must leave something net, and cannot be negative.
 */
export function splitVatInput(
  grossMinor: number,
  spec: VatInputSpec,
  currency: CurrencyCode,
): { netMinor: number; vatMinor: number } {
  requireMinor(grossMinor, 'Amount');
  if ((spec.rateBp === undefined) === (spec.amountMinor === undefined)) {
    throw new AccountingError('Give the VAT rate or the VAT amount, not both and not neither');
  }
  let vatMinor: number;
  if (spec.rateBp !== undefined) {
    if (!Number.isInteger(spec.rateBp) || spec.rateBp <= 0 || spec.rateBp > 5000) {
      throw new AccountingError('The VAT rate must be between 0.01% and 50%');
    }
    const percent = `${Math.floor(spec.rateBp / 100)}.${String(spec.rateBp % 100).padStart(2, '0')}`;
    vatMinor = vatFromInclusive(money(grossMinor, currency), percent).vat.amountMinor;
  } else {
    vatMinor = spec.amountMinor as number;
    requireMinor(vatMinor, 'VAT', true);
  }
  if (vatMinor >= grossMinor) {
    throw new AccountingError('The VAT cannot be the whole amount: something must be left as cost');
  }
  return { netMinor: grossMinor - vatMinor, vatMinor };
}

/** The 1040 debit for a purchase, or nothing when there is no VAT. */
function vatDebit(vat: VatLine | undefined, grossMinor: number, d: Dimensions): JournalLineDraft[] {
  if (!vat || vat.vatMinor === 0) return [];
  return [
    debit('1040', vat.vatMinor, {
      ...d,
      invoiceNo: vat.invoiceNo,
      supplierVatNo: vat.supplierVatNo,
      vatNetMinor: String(grossMinor - vat.vatMinor),
    }),
  ];
}

export interface MoneyOutInput {
  readonly category: MoneyOutCategoryKey;
  /** What left the wallet: VAT-inclusive when `vat` is given. */
  readonly amountMinor: number;
  readonly wallet: WalletRef;
  readonly vat?: VatLine | undefined;
  readonly dimensions: Dimensions;
}

/**
 * Money out by category: Dr the category's account; Cr the wallet's account. With `vat`, the cost is
 * the net and the supplier's VAT is a separate debit to 1040.
 */
export function moneyOutLines(input: MoneyOutInput): JournalLineDraft[] {
  requireMinor(input.amountMinor, 'Amount');
  const mapped = MONEY_OUT_CATEGORY_ACCOUNT[input.category];
  const vatMinor = input.vat?.vatMinor ?? 0;
  if (vatMinor >= input.amountMinor) {
    throw new AccountingError('The VAT cannot be the whole amount: something must be left as cost');
  }
  return checked([
    debit(mapped.account, input.amountMinor - vatMinor, input.dimensions),
    ...vatDebit(input.vat, input.amountMinor, input.dimensions),
    credit(input.wallet.accountCode, input.amountMinor, {
      ...input.dimensions,
      walletId: input.wallet.walletId,
    }),
  ]);
}

// ------------------------------------------------------------------ money in

export type MoneyInCategoryKey = 'owner_capital' | 'loan' | 'other_income' | 'refund_received';

export const MONEY_IN_CATEGORY_ACCOUNT: Readonly<
  Record<MoneyInCategoryKey, { readonly account: string | undefined; readonly label: string }>
> = {
  owner_capital: { account: '3000', label: 'Owner capital' },
  loan: { account: '2200', label: 'Loan received' },
  other_income: { account: '4900', label: 'Other income' },
  /** The account is the cost being refunded (`refundOf`), else Office and supplies. */
  refund_received: { account: undefined, label: 'Refund received' },
};

export interface MoneyInInput {
  readonly category: MoneyInCategoryKey;
  /** For `refund_received`: the cost category the refund reduces (default `other`). */
  readonly refundOf?: MoneyOutCategoryKey | undefined;
  readonly amountMinor: number;
  readonly wallet: WalletRef;
  readonly dimensions: Dimensions;
}

/** The account credited for a kind of money in. */
export function moneyInAccount(
  category: MoneyInCategoryKey,
  refundOf?: MoneyOutCategoryKey | undefined,
): string {
  return (
    MONEY_IN_CATEGORY_ACCOUNT[category].account ??
    MONEY_OUT_CATEGORY_ACCOUNT[refundOf ?? 'other'].account
  );
}

/** Money in that is not a shipment payment (14 §14.4): Dr the wallet's account, Cr the category's account. */
export function moneyInLines(input: MoneyInInput): JournalLineDraft[] {
  requireMinor(input.amountMinor, 'Amount');
  return checked([
    debit(input.wallet.accountCode, input.amountMinor, {
      ...input.dimensions,
      walletId: input.wallet.walletId,
    }),
    credit(moneyInAccount(input.category, input.refundOf), input.amountMinor, input.dimensions),
  ]);
}

export interface PartnerRemittanceInput {
  readonly amountMinor: number;
  readonly wallet: WalletRef;
  readonly dimensions: Dimensions;
}

/** A partner remits cash it collected for us (12 §12.6): Dr the wallet that receives it, Cr 1003. */
export function partnerRemittanceLines(input: PartnerRemittanceInput): JournalLineDraft[] {
  requireMinor(input.amountMinor, 'Amount remitted');
  return checked([
    debit(input.wallet.accountCode, input.amountMinor, {
      ...input.dimensions,
      walletId: input.wallet.walletId,
    }),
    credit('1003', input.amountMinor, input.dimensions),
  ]);
}

export interface MoveMoneyInput {
  readonly amountMinor: number;
  readonly from: WalletRef;
  readonly to: WalletRef;
  readonly dimensions: Dimensions;
}

/** Move money: Dr the receiving wallet's account, Cr the giving wallet's. Same currency only (Phase 2). */
export function moveMoneyLines(input: MoveMoneyInput): JournalLineDraft[] {
  requireMinor(input.amountMinor, 'Amount');
  if (input.from.walletId === input.to.walletId) {
    throw new AccountingError('Money cannot move from a wallet to itself');
  }
  return checked([
    debit(input.to.accountCode, input.amountMinor, {
      ...input.dimensions,
      walletId: input.to.walletId,
    }),
    credit(input.from.accountCode, input.amountMinor, {
      ...input.dimensions,
      walletId: input.from.walletId,
    }),
  ]);
}

export interface TillOpenInput {
  readonly floatMinor: number;
  readonly till: WalletRef;
  readonly safe: WalletRef;
  readonly dimensions: Dimensions;
}

/** Opening float: cash leaves the safe for the drawer (Dr till, Cr safe). */
export function tillOpenLines(input: TillOpenInput): JournalLineDraft[] {
  requireMinor(input.floatMinor, 'Opening float', true);
  if (input.floatMinor === 0) return [];
  return moveMoneyLines({
    amountMinor: input.floatMinor,
    from: input.safe,
    to: input.till,
    dimensions: input.dimensions,
  });
}

export interface TillCloseInput {
  /** Opening float plus the cash movement the books recorded on this till. */
  readonly expectedMinor: number;
  readonly countedMinor: number;
  readonly till: WalletRef;
  readonly safe: WalletRef;
  readonly dimensions: Dimensions;
}

/**
 * Till close (06 §6.3): the counted cash goes to the safe, the drawer's book cash is cleared, and
 * the difference lands in 6200. Over: Cr 6200. Short: Dr 6200. Always balanced.
 */
export function tillCloseLines(input: TillCloseInput): JournalLineDraft[] {
  requireMinor(input.expectedMinor, 'Expected cash', true);
  requireMinor(input.countedMinor, 'Counted cash', true);
  const variance = input.countedMinor - input.expectedMinor;
  const tillDims = { ...input.dimensions, walletId: input.till.walletId };
  const safeDims = { ...input.dimensions, walletId: input.safe.walletId };
  return checked([
    debit(input.safe.accountCode, input.countedMinor, safeDims),
    credit(input.till.accountCode, input.expectedMinor, tillDims),
    variance < 0
      ? debit('6200', -variance, input.dimensions)
      : credit('6200', variance, input.dimensions),
  ]);
}

export interface DriverHandoverInput {
  /** What the books say the driver holds (account 1001). */
  readonly heldMinor: number;
  /** What the cashier counted. */
  readonly countedMinor: number;
  readonly driver: WalletRef;
  readonly till: WalletRef;
  readonly dimensions: Dimensions;
}

/**
 * Driver hands cash to a cashier (06 §6.3): Dr the till for the counted cash, Cr 1001 for what the
 * driver held, and the difference to 6200 (short: Dr; over: Cr). Always balanced.
 */
export function driverHandoverLines(input: DriverHandoverInput): JournalLineDraft[] {
  requireMinor(input.heldMinor, 'Held cash', true);
  requireMinor(input.countedMinor, 'Counted cash', true);
  const variance = input.countedMinor - input.heldMinor;
  const tillDims = { ...input.dimensions, walletId: input.till.walletId };
  const driverDims = { ...input.dimensions, walletId: input.driver.walletId };
  return checked([
    debit(input.till.accountCode, input.countedMinor, tillDims),
    credit(input.driver.accountCode, input.heldMinor, driverDims),
    variance < 0
      ? debit('6200', -variance, input.dimensions)
      : credit('6200', variance, input.dimensions),
  ]);
}

export interface DayCloseInput {
  /** Counted minus book cash. */
  readonly varianceMinor: number;
  readonly safe: WalletRef;
  readonly dimensions: Dimensions;
}

/** Day close: the branch's remaining difference, 6200 against the safe's cash. */
export function dayCloseLines(input: DayCloseInput): JournalLineDraft[] {
  if (!Number.isSafeInteger(input.varianceMinor))
    throw new AccountingError('Variance must be whole minor units');
  if (input.varianceMinor === 0) return [];
  const safeDims = { ...input.dimensions, walletId: input.safe.walletId };
  return input.varianceMinor > 0
    ? checked([
        debit(input.safe.accountCode, input.varianceMinor, safeDims),
        credit('6200', input.varianceMinor, input.dimensions),
      ])
    : checked([
        debit('6200', -input.varianceMinor, input.dimensions),
        credit(input.safe.accountCode, -input.varianceMinor, safeDims),
      ]);
}

/** Debit minus credit, or credit minus debit for credit-normal accounts: the sign a balance carries. */
export function signedEffect(accountCode: string, debitMinor: number, creditMinor: number): number {
  const normal = getAccount(accountCode)?.normal ?? 'debit';
  return normal === 'debit' ? debitMinor - creditMinor : creditMinor - debitMinor;
}

// ------------------------------------------------------------ supplier bills

export interface BillRecordInput {
  /** What the supplier invoiced, in full, VAT included: all of it is owed (Cr 2000). */
  readonly totalMinor: number;
  /** The supplier's VAT inside the total: Dr 1040 (06 §6.3). */
  readonly vat?: VatLine | undefined;
  /** The accrual this bill clears (Dr 2050). */
  readonly clearedMinor: number;
  /** Where the part of the bill that was never accrued lands (5055 for a partner, else the category). */
  readonly costAccount: string;
  readonly dimensions: Dimensions;
}

/**
 * Supplier bill received (06 §6.3): Dr 2050 for the accrual it clears, Dr the cost account for any
 * difference between the net bill and that accrual (Cr when the bill is cheaper than the accrual),
 * Dr 1040 for the supplier's VAT, Cr 2000 for the whole bill.
 */
export function billRecordLines(input: BillRecordInput): JournalLineDraft[] {
  requireMinor(input.totalMinor, 'Bill total');
  requireMinor(input.clearedMinor, 'Accrual cleared', true);
  const vatMinor = input.vat?.vatMinor ?? 0;
  if (vatMinor >= input.totalMinor) {
    throw new AccountingError('The VAT cannot be the whole amount: something must be left as cost');
  }
  const difference = input.totalMinor - vatMinor - input.clearedMinor;
  const d = input.dimensions;
  return checked([
    debit('2050', input.clearedMinor, d),
    ...vatDebit(input.vat, input.totalMinor, d),
    difference >= 0
      ? debit(input.costAccount, difference, d)
      : credit(input.costAccount, -difference, d),
    credit('2000', input.totalMinor, d),
  ]);
}

export interface BillPaymentInput {
  /** Cash paid from the wallet. May be 0 when only collections are offset. */
  readonly amountMinor: number;
  /** Settled by cash the partner already collected for us (Cr 1003). */
  readonly offsetCollectionsMinor?: number | undefined;
  /** Required with an offset: the partner whose collections are used. */
  readonly partnerId?: string | undefined;
  readonly wallet: WalletRef;
  readonly dimensions: Dimensions;
}

/**
 * Bill paid (06 §6.3): Dr 2000 accounts payable for everything settled; Cr the wallet's account for
 * the cash and Cr 1003 for the part offset against the partner's collections (12 §12.6).
 */
export function billPaymentLines(input: BillPaymentInput): JournalLineDraft[] {
  const offset = input.offsetCollectionsMinor ?? 0;
  requireMinor(input.amountMinor, 'Payment', offset > 0);
  requireMinor(offset, 'Offset', true);
  if (offset > 0 && !input.partnerId) {
    throw new AccountingError('Only a partner bill can be offset against collections');
  }
  return checked([
    debit('2000', input.amountMinor + offset, input.dimensions),
    credit(input.wallet.accountCode, input.amountMinor, {
      ...input.dimensions,
      walletId: input.wallet.walletId,
    }),
    credit('1003', offset, { ...input.dimensions, partnerId: input.partnerId ?? '' }),
  ]);
}

export interface PartnerCollectionInput {
  readonly amountMinor: number;
  /** 2040 for duties and taxes collected for customs, else 4010 (destination charges, 06 §6.3). */
  readonly creditAccount: '4010' | '2040';
  readonly dimensions: Dimensions;
}

/**
 * Destination charges a partner collected from a consignee (12 §12.6): Dr 1003 partner collections
 * receivable (until the partner remits them), Cr the charge's revenue or pass-through liability.
 */
export function partnerCollectionLines(input: PartnerCollectionInput): JournalLineDraft[] {
  requireMinor(input.amountMinor, 'Amount collected');
  return checked([
    debit('1003', input.amountMinor, input.dimensions),
    credit(input.creditAccount, input.amountMinor, input.dimensions),
  ]);
}

const DUTY_LABEL = /\b(duty|duties|tax|taxes|customs|vat)\b/i;

/** Duties and taxes are passed through (2040); every other destination charge is revenue (4010). */
export function collectionCreditAccount(chargeLabels: readonly string[]): '4010' | '2040' {
  return chargeLabels.length > 0 && chargeLabels.every((l) => DUTY_LABEL.test(l)) ? '2040' : '4010';
}

// ------------------------------------------------------------ journal indexes

/** Every account a journal touches, sorted: stored on the journal so queries can find it by account. */
export function journalAccountCodes(
  lines: readonly Pick<JournalLineDraft, 'accountCode'>[],
): string[] {
  return [...new Set(lines.map((l) => l.accountCode))].sort();
}

/** Every wallet a journal names, sorted: stored on the journal so a bank account finds its entries. */
export function journalWalletIds(lines: readonly Pick<JournalLineDraft, 'dimensions'>[]): string[] {
  const ids = lines.map((l) => l.dimensions['walletId']).filter((w): w is string => !!w);
  return [...new Set(ids)].sort();
}
